import {
  State,
  toDataSourceRequestString,
  translateDataSourceResultGroups,
} from '@progress/kendo-data-query';
import { GridDataStateChangeEvent } from '@progress/kendo-react-grid';
import { useGridState, useToasts } from 'Context';
import {
  isNullEmptyOrUndefined,
  useApiWorker,
  useSafeStateUpdate,
  withoutUndefinedProperties,
} from 'Utilities';
import Axios from 'axios';
import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import { mergeByKey } from '../helpers/mergeByKey';
import { AsyncGridProps } from '../types';

export const useAsyncGridState = ({
  condition = true,
  search = {},
  additionalFilters = {},
  refreshToggle,
  readEndpoint,
  onFetchSuccess,
  onlyFetchWithFilters,
  refreshOnSearchChange,
  pageable,
  pageSize,
  group,
  apiVersion,
  dataItemKey = 'id',
  ...otherProps
}: Partial<AsyncGridProps>) => {
  const gridState = useGridState();
  const API = useApiWorker();
  const toast = useToasts();
  const safeStateUpdate = useSafeStateUpdate();
  const pageNeedsReset = useRef<boolean>(false);
  const isInitialLoad = useRef<boolean>(true);

  const { isFetching, setIsFetching } = gridState;
  const pageSettings = pageable
    ? { skip: 0, take: pageSize }
    : { skip: 0, take: pageSize ? pageSize : 50 };
  const [dataState, setDataState] = useState<State>({
    group,
    ...pageSettings,
  });
  const hasData = gridState.total > 0;
  const fullSearch = { ...search, ...additionalFilters };

  const toFirstPage = (state: State): State => ({ ...state, skip: 0 });

  const hasSearchUpdated = () => {
    return (
      !isEqual(gridState.search, fullSearch) &&
      !(
        isNullEmptyOrUndefined(gridState.search) &&
        isNullEmptyOrUndefined(fullSearch)
      )
    );
  };

  const restoreScrollPosition = (position: number) => {
    const gridContent =
      document.querySelector<HTMLDivElement>('.k-grid-content');
    if (gridContent) {
      gridContent.scrollLeft = position || 0;
    }
  };

  const resetStateToFirstPage = (): boolean => {
    pageNeedsReset.current = false;
    const target = toFirstPage(dataState);
    if (isEqual(dataState, target)) return false;
    setDataState(target);
    return true;
  };

  const compareDataStatesWithoutSkip = (
    updatedDataState: State,
    currentDataState: State
  ) => {
    const { skip: updatedSkip, ...updatedDataStateNoSkip } = updatedDataState;
    const { skip: currentSkip, ...currentDataStateNoSkip } = currentDataState;
    return isEqual(updatedDataStateNoSkip, currentDataStateNoSkip);
  };

  const onDataStateChange = ({ dataState: data }: GridDataStateChangeEvent) => {
    const sanitizedIncoming = withoutUndefinedProperties(data);
    const sanitizedCurrent = withoutUndefinedProperties(dataState);
    
    if (!compareDataStatesWithoutSkip(sanitizedIncoming, sanitizedCurrent)) {
      setDataState(toFirstPage(sanitizedIncoming));
    } else if (!isEqual(sanitizedIncoming, sanitizedCurrent)) {
      setDataState(sanitizedIncoming);
    }
  };

  const fetchData = async (fetchDataState: State = {}, showLoading = true, forceRerender = false) => {
    const gridContent =
      document.querySelector<HTMLDivElement>('.k-grid-content');
    const previousScroll = gridContent?.scrollLeft ?? 0;
    const queryStr = toDataSourceRequestString(fetchDataState);
    const hasGroups = !!(fetchDataState.group && fetchDataState.group.length);
    const cancelTokenSource = Axios.CancelToken.source();

    const stateUpdateOrCancel = (updateFunc?: () => any) => {
      safeStateUpdate(updateFunc, cancelTokenSource.cancel);
    };

    const axiosConfig = {
      headers: {
        Accept: '*/*',
        'Content-Type': 'application/json',
        ...(apiVersion && { 'x-api-version': apiVersion }),
      },
    };

    if (showLoading) stateUpdateOrCancel(() => setIsFetching(true));

    try {
      const { data } = await API.post(
        `${readEndpoint}?${queryStr}`,
        fullSearch,
        axiosConfig
      );

      onFetchSuccess && onFetchSuccess(data);

      const formattedData = hasGroups
        ? translateDataSourceResultGroups(data.data)
        : data.data;

      stateUpdateOrCancel(() => {
        if (gridState.total !== data.total) {
          gridState.setTotal(data.total);
        }

        if (hasGroups) {
          if (!isEqual(formattedData, gridState.data)) {
            gridState.setData(formattedData);
          }
        } else {
          const prev = Array.isArray(gridState.data) ? gridState.data : [];
          const { changed, data: merged } = mergeByKey(
            prev,
            formattedData,
            dataItemKey
          );
          if (changed || forceRerender) {
            gridState.setData(merged);
          }
        }
      });
    } catch (err) {
      toast.error('Error fetching table data');
      console.log(err);
    } finally {
      if (showLoading) stateUpdateOrCancel(() => setIsFetching(false));
      if (gridContent) {
        restoreScrollPosition(previousScroll);
      }
    }
  };

  const fetchDataConditionally = (showLoading = true, forceRerender = false) => {
    if (pageNeedsReset.current) {
      if (resetStateToFirstPage()) return;
    }

    if (condition && !isNullEmptyOrUndefined(gridState.state)) {
      if (
        onlyFetchWithFilters === undefined ||
        (onlyFetchWithFilters && !isNullEmptyOrUndefined(fullSearch))
      ) {
        fetchData(gridState.state, showLoading, forceRerender);
        isInitialLoad.current = false;
      }
    }
  };

  useEffect(() => {
    fetchDataConditionally(true);
  }, [gridState.refreshValue, gridState.reloadValue, refreshToggle]);

  useEffect(() => {
    if (!isNullEmptyOrUndefined(gridState.state)) {
      fetchDataConditionally(true, true);
    }
  }, [gridState.state]);

  useEffect(() => {
    if (refreshOnSearchChange) {
      fetchDataConditionally(true);
    }
  }, [refreshOnSearchChange, gridState.search]);

  useEffect(() => {
    if (!isEqual(gridState.state, dataState)) {
      gridState.setState(dataState);
    }
  }, [dataState]);

  useEffect(() => {
    if (hasSearchUpdated()) {
      if (!isInitialLoad.current) {
        pageNeedsReset.current = true;
      }
      gridState.setSearch(fullSearch);
    }
  }, [fullSearch]);

  return {
    hasData,
    isFetching,
    onDataStateChange,
    total: gridState.total,
    dataState: gridState.state,
    data: gridState.data,
    resetStateToFirstPage,
    setDataState,
    fetchSilently: () => fetchDataConditionally(false),
  };
};
